import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnvelopeSimple, Phone, MapPin, Clock, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "General", message: "" });
  const [sending, setSending] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support/contact", form);
      toast.success("Message received — we'll reply within one working day, in-shaa-Allah.");
      setForm({ name: "", email: "", phone: "", topic: "General", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16" data-testid="contact-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="label-eyebrow text-brand-terracotta">Contact</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 text-brand-ink">Speak with us.</h1>
        <p className="mt-6 text-brand-muted leading-relaxed">
          Have a question about membership, a request from your family, or a
          story to share? We would be honoured to hear from you.
        </p>
      </div>

      <div className="mt-16 grid md:grid-cols-12 gap-10">
        {/* Details */}
        <aside className="md:col-span-5 space-y-6">
          <Card icon={EnvelopeSimple} label="Email" value="rashidhussain896@gmail.com" href="mailto:rashidhussain896@gmail.com" />
          <Card icon={Phone} label="Phone / WhatsApp" value="+91 98928 10623" href="tel:+919892810623" />
          <Card icon={MapPin} label="Serving members in" value="India · GCC · UK · North America · Australia" />
          <Card icon={Clock} label="Response time" value="Within one working day, in-shaa-Allah" />

          <div className="bg-brand-bone border border-brand-line p-6 mt-6">
            <div className="label-eyebrow text-brand-muted">A note on adab</div>
            <p className="mt-3 text-sm text-brand-muted leading-relaxed">
              We prioritise messages from family members (parents, walis,
              siblings) on behalf of a member — please mention who you are
              writing for and we'll respond with the appropriate discretion.
            </p>
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={submit} className="md:col-span-7 bg-white border border-brand-line p-8 md:p-10 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="label-eyebrow text-brand-muted">Your name</Label>
              <Input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="contact-name" className="mt-2 h-12 rounded-none border-brand-line" placeholder="Ahmed bin Yusuf" />
            </div>
            <div>
              <Label className="label-eyebrow text-brand-muted">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="contact-email" className="mt-2 h-12 rounded-none border-brand-line" placeholder="you@family.com" />
            </div>
            <div>
              <Label className="label-eyebrow text-brand-muted">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contact-phone" className="mt-2 h-12 rounded-none border-brand-line" placeholder="+91 98765 43210" />
            </div>
            <div>
              <Label className="label-eyebrow text-brand-muted">Topic</Label>
              <Select value={form.topic} onValueChange={(v) => upd("topic", v)}>
                <SelectTrigger data-testid="contact-topic" className="mt-2 h-12 rounded-none border-brand-line"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General enquiry</SelectItem>
                  <SelectItem value="Membership">Membership</SelectItem>
                  <SelectItem value="Family enquiry">Family enquiry</SelectItem>
                  <SelectItem value="Payment">Payment</SelectItem>
                  <SelectItem value="Story submission">Story submission</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow text-brand-muted">Message</Label>
            <Textarea required rows={6} value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="contact-message" className="mt-2 rounded-none border-brand-line" placeholder="Tell us about your enquiry…" />
          </div>
          <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full h-13 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">
            <PaperPlaneTilt size={16} weight="fill" className="mr-2" /> {sending ? "Sending…" : "Send message"}
          </Button>
          <p className="text-[11px] text-brand-muted">We reply from a real inbox — never bots. Your details are never shared with third parties.</p>
        </form>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <Icon size={22} weight="duotone" className="text-brand-terracotta mt-1" />
      <div>
        <div className="label-eyebrow text-brand-muted">{label}</div>
        <div className="mt-1 text-brand-ink">{value}</div>
      </div>
    </div>
  );
  return (
    <div className="border border-brand-line bg-white p-5 hover:border-brand-terracotta/40 transition-colors">
      {href ? <a href={href}>{content}</a> : content}
    </div>
  );
}
